import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import ProductCard from '../../../miniapp/components/ProductCard';
import { WEBSITE_PRODUCT_CARD_SIZES } from '../../../utils/responsiveImages';
import {
  getLocalizedEntityField,
  getNormalizedLanguageCode,
} from '../../../miniapp/i18n/localization';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { formatCurrency } from '../../utils/formatters';
import Icon from '../../../miniapp/ui-kit/Icon';
import { parseEntitySegment } from '../../../utils/websiteSsr/paths';
import { manufacturerPath, productPath } from '../../../utils/websiteSsr/paths';

export default function CategoryScreen() {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const { t, i18n } = useTranslation();
  const { catalogLoading, categories, products, addToCart, removeFromCart, cartItems } =
    useWebsiteAppContext();
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);

  const parsedCategoryId = parseEntitySegment(categorySlug);
  const isValidId = parsedCategoryId !== null;
  const selectedCategory = useMemo(
    () =>
      isValidId ? (categories.find((category) => category.id === parsedCategoryId) ?? null) : null,
    [categories, isValidId, parsedCategoryId],
  );
  const categoryProducts = useMemo(
    () => (isValidId ? products.filter((product) => product.categoryId === parsedCategoryId) : []),
    [isValidId, parsedCategoryId, products],
  );
  const cartQuantityByProductKey = useMemo(() => {
    const quantityByKey = new Map<string, number>();

    cartItems.forEach((item) => {
      const key = `${item.product.id}-${item.weight}`;
      quantityByKey.set(key, (quantityByKey.get(key) ?? 0) + item.quantity);
    });

    return quantityByKey;
  }, [cartItems]);

  if (catalogLoading) {
    return (
      <div className="grid gap-10" aria-busy="true" aria-label={t('common.loading')}>
        <div className="min-h-[260px] animate-pulse rounded-4xl bg-secondary md:min-h-[340px]" />
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="grid gap-3" aria-hidden="true">
              <div className="aspect-square animate-pulse rounded-xl bg-secondary" />
              <div className="h-4 w-3/4 animate-pulse rounded-full bg-secondary" />
              <div className="h-4 w-1/3 animate-pulse rounded-full bg-secondary" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!isValidId || !selectedCategory) {
    return (
      <section className="mx-auto flex min-h-[360px] max-w-xl flex-col items-center justify-center text-center">
        <Icon name="search_off" className="text-5xl text-text-secondary" />
        <h1 className="m-0 mt-4 text-2xl font-bold tracking-tight text-text">
          {t('website.category.notFound')}
        </h1>
        <Link
          to="/catalog"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-6 py-2.5 font-semibold text-on-accent transition-colors hover:bg-accent-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          {t('website.notFound.backToCatalog')}
        </Link>
      </section>
    );
  }

  const categoryName = getLocalizedEntityField(selectedCategory, 'name', languageCode);
  const categoryImageUrl = selectedCategory.image?.url ?? selectedCategory.imageUrl ?? null;

  return (
    <div className="grid gap-10 md:gap-12">
      <nav aria-label={t('website.notFound.backToCatalog')}>
        <Link
          to="/catalog"
          className="inline-flex min-h-11 items-center gap-2 rounded-full px-1 pr-3 text-sm font-semibold text-text-secondary transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Icon name="arrow_back" className="text-xl" />
          {t('website.notFound.backToCatalog')}
        </Link>
      </nav>

      <header className="relative -mt-4 flex min-h-[260px] items-end overflow-hidden rounded-4xl bg-secondary shadow-ambient md:min-h-[340px]">
        {categoryImageUrl ? (
          <img
            src={categoryImageUrl}
            alt={categoryName}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <Icon name="spa" className="text-7xl text-text-secondary/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,var(--color-hero-overlay-top)_0%,var(--color-hero-overlay-bottom)_100%)]" />
        <div className="relative z-10 max-w-3xl px-6 py-7 text-[var(--color-hero-chip-text)] md:px-10 md:py-9">
          <p className="m-0 text-xs font-bold uppercase tracking-[0.1em] text-[var(--color-hero-chip-text)]">
            {t('catalog.productsTitle')}
          </p>
          <h1 className="editorial-title m-0 mt-2 text-4xl font-bold leading-tight md:text-5xl">
            {categoryName}
          </h1>
          <p className="m-0 mt-4 text-sm font-semibold text-[var(--color-hero-chip-text)]">
            {t('common.productsCount', { count: categoryProducts.length })}
          </p>
        </div>
      </header>

      <section aria-labelledby="category-products-title">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h2
              id="category-products-title"
              className="m-0 text-2xl font-bold tracking-tight text-text"
            >
              {t('website.category.productsInCategory', { name: categoryName })}
            </h2>
          </div>
          <span className="shrink-0 text-sm font-medium text-text-secondary">
            {t('common.productCount', { count: categoryProducts.length })}
          </span>
        </div>

        {categoryProducts.length > 0 ? (
          <div
            className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            aria-live="polite"
          >
            {categoryProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                href={productPath(product)}
                manufacturerHref={product.manufacturer ? manufacturerPath(product.manufacturer) : undefined}
                getCartQuantity={(weight) =>
                  cartQuantityByProductKey.get(`${product.id}-${weight}`) ?? 0
                }
                onOpen={() => navigate(`/product/${product.id}`)}
                onAddToCart={(weight) => addToCart(product.id, weight)}
                onRemoveFromCart={(weight) => removeFromCart(product.id, weight)}
                onOpenManufacturer={(id) => navigate(`/manufacturer/${id}`)}
                formatCurrency={formatCurrency}
                imageSizes={WEBSITE_PRODUCT_CARD_SIZES}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[260px] flex-col items-center justify-center rounded-4xl bg-primary px-6 text-center">
            <Icon name="inventory_2" className="text-5xl text-text-secondary/40" />
            <h3 className="m-0 mt-4 text-lg font-semibold text-text">{t('catalog.noProducts')}</h3>
            <p className="m-0 mt-2 max-w-md text-text-secondary">
              {t('catalog.noProductsDescription')}
            </p>
            <Link
              to="/catalog"
              className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-6 py-2.5 font-semibold text-on-accent transition-colors hover:bg-accent-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              {t('website.notFound.backToCatalog')}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
